import { useMemo, useState } from "react";
import styles from "./CloneSettings.module.scss";
import { WLP202603 } from "../../../../../../config";
import cn from "classnames";
import { useCloneSettingsControl } from "./useCloneSettingsControl";
import SmallLink from "@components/global/new/SmallLink/SmallLink";
import { Tooltip, Transfer, Button } from "antd";
import type { TransferDirection } from "antd/es/transfer";
import { ICloneSettingsProps } from "./CloneSettings.props";
import { ISavedMinerSetting } from "@store/savedMinerSettings/actionTypes";
import { makeNotifs } from "@utils/makeNotifs";
import { useIntl } from "react-intl";
import ListSavedSettings from "./ListSavedSettings/ListSavedSettings";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";


export interface IDataTransfer {
  readonly key: keyof Omit<ISavedMinerSetting, "cloned_miner">;
  readonly title: keyof Omit<ISavedMinerSetting, "cloned_miner">;
  readonly value: ISavedMinerSetting[keyof Omit<
    ISavedMinerSetting,
    "cloned_miner"
  >];
}

export default function CloneSettings({
  currSettings,
  miner,
}: ICloneSettingsProps) {
  const { formatMessage } = useIntl();
  const [opened, setOpened] = useState(false);

  const source = useMemo(() => {
    const data: IDataTransfer[] = [];
    Object.keys(currSettings).forEach((item) => {
      const key = item as keyof typeof currSettings;
      const value: IDataTransfer["value"] = currSettings[key];
      data.push({ key, title: key, value });
    });
    return data;
  }, [currSettings]);

  return (
    <div className={styles.wrapper}>
      <Tooltip
        placement='left'
        trigger='click'
        title={
          <TooltipTransfer dataSource={source} miner={miner} opened={opened} />
        }
        color='#fff'
        //overlayInnerStyle={{ width: "max-content" }}
        styles={{
          body: {
            width: "max-content"
          }
        }}
        ref={(r: any) => setOpened(r?.state?.popupVisible || false)}
      >
        <SmallLink className={styles.link}>
          {formatMessage({ id: "machines.more.clone_settings" })}
        </SmallLink>
      </Tooltip>
    </div>
  );
}

export interface ITransferProps {
  readonly dataSource: IDataTransfer[];
  readonly miner: string;
  readonly opened: boolean;
}

const TooltipTransfer = ({ ...props }: ITransferProps) => {
  const control = useCloneSettingsControl(props);

  return (
    <Tooltip
      color='#f2f1f1'
      open={control.savedSettings.length > 0 && props.opened}
      placement='rightBottom'
      title={<ListSavedSettings />}
      destroyTooltipOnHide
    >
      <div className={styles.transfer_wrapper}>
        <Transfer
          dataSource={props.dataSource}
          targetKeys={control.targetKeys}
          selectedKeys={control.selectedKeys}
          onChange={control.handleChange}
          onSelectChange={control.handleSelectChange}
          operationStyle={{ transform: "rotate(90deg)", margin: "10px 0" }}
          render={control.renderItem}
          oneWay
          className={styles.transfer}
          footer={
            control.targetKeys.length > 0
              ? (props, info) => (
                <TransferFooter
                  direction={info?.direction}
                  onSave={control.onSave}
                />
              )
              : undefined
          }
        />
      </div>
    </Tooltip>
  );
};

interface IFooter {
  readonly direction: TransferDirection | undefined;

  onSave(): void;
}
const TransferFooter = ({ direction, onSave }: IFooter) => {
  const { ok } = makeNotifs();
  const { onSuccess } = useGlobalNotifs();
  const [loading, setLoading] = useState<boolean>(false);

  if (direction === "left") return <></>;

  const onSaveHandler = () => {
    setLoading(true);

    setTimeout(() => {
      onSave();
      setLoading(false);
      onSuccess(ok.success);
    }, 500);
  };

  return (
    <div className={styles.footer}>
      <Button
        className={styles.save_btn}
        size='small'
        loading={loading}
        onClick={onSaveHandler}
      >
        Save
      </Button>
    </div>
  );
};
